import { Markup } from 'interweave'
import React, { FunctionComponent, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { OrderKey } from '../../entities/ConfigurationResponse'
import configuredModelIdSelector from '../../redux/selectors/models/configuredModelIdSelector'
import style from '../overview-view/overviewView.css'
import useTranslation from '../../hooks/useTranslation'

export type Props = {
    orderKey: OrderKey
}

const extractEquipmentCodes = (modelId: string): string[] => {
    if (!modelId) {
        return []
    }

    const beforeDash = modelId.split("-")[0]

    const parts = beforeDash
        .split("\\")
        .map(p => p.trim())
        .filter(Boolean)

    if (parts.length <= 2) {
        return []
    }

    return parts.slice(2)
}

const formatOrderKeySegment = (segment: string, index: number, equipmentCodes: string[]): string => {
    if (index === 0 || index === 1 || segment.length < 2) {
        return segment
    }

    const codeToCheck = segment.length === 2 ? segment : segment.slice(-3)
    const isBaseEquipment = equipmentCodes.some(code => code.includes(codeToCheck))

    return isBaseEquipment ? segment : `<strong>${segment}</strong>`
}

const OrderKeyFormatted: FunctionComponent<Props> = ({ orderKey }) => {
    const modelId = useSelector(configuredModelIdSelector)
    const { t } = useTranslation()

    const equipmentCodes = useMemo(() =>
        extractEquipmentCodes(modelId),
        [modelId],
    )

    const formattedOrderKey = useMemo(() => {
        if (!orderKey) {return orderKey}

        return orderKey
            .split(' ')
            .filter(Boolean)
            .map((segment, index) => formatOrderKeySegment(segment, index, equipmentCodes))
            .join(' ')
    }, [orderKey, equipmentCodes])

    if (!orderKey) { return null }

    return (
        <>
            <h2 className={style.subTitle}>{t('overview.orderKey')}</h2>
            <p data-testid="order-key">
                <Markup content={formattedOrderKey} />
            </p>
        </>
    )
}

export default OrderKeyFormatted
